import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Button, DatePicker, Input, Select, Table } from "antd";
import config from "../../../config";
import authHeader from "../../Authentication/authHeader";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

export default function CustomerLedgerReport({ tabChange }) {
  dayjs.extend(utc);
  dayjs.extend(timezone);
  const TIMEZONE = "Asia/Dhaka";
  const { Option } = Select;
  const { Search } = Input;
  const { RangePicker } = DatePicker;
  const [supplierLedgers, setSupplierLedgers] = useState([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [customerType, setCustomerType] = useState(["Regular", "Retailer"]);
  const [centerList, setCenterList] = useState([]);

  const [selectedCustomerType, setSelectedCustomerType] = useState("");
  const [selectedDateRange, setSelectedDateRange] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCenter, setSelectedCenter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const handleDateRange = (value) => {
    setCurrentPage(1);
    if (Array.isArray(value) && value.length === 2 && value[0] && value[1]) {
      let startDate = dayjs.tz(value[0], TIMEZONE).startOf("day");
      let endDate = dayjs.tz(value[1], TIMEZONE).endOf("day");

      // ✅ Store ISO strings with timezone offset
      setSelectedDateRange([
        startDate.format("YYYY-MM-DDTHH:mm:ssZ"), // e.g. 2025-09-21T00:00:00+06:00
        endDate.format("YYYY-MM-DDTHH:mm:ssZ"),
      ]);
    } else {
      setSelectedDateRange(null);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    axios
      .get(`${config.baseUrl}/inventory/center_list/`, {
        headers: authHeader(),
      })
      .then((response) => {
        setCenterList(response?.data);
      })
      .catch((error) => {
        Swal.fire({
          title: "Error",
          text: error,
          confirmButtonText: "Ok",
          icon: "error",
        });
      });
  }, []);

  const fetchData = async () => {
    try {
      setSalesLoading(true);
      const response = await axios.get(
        `${config.baseUrl}/inventory/customer_ledger_list/`,
        {
          headers: authHeader(),
          params: {
            dateRange: selectedDateRange,
            customer_type: selectedCustomerType,
            search: search,
            exclude_corporate: true,
            center_id: selectedCenter,
            page: currentPage,
          },
        }
      );
      const resData = Array.isArray(response?.data?.results)
        ? response?.data?.results
        : [];
      setTotalItems(response?.data?.count || 0);
      setSupplierLedgers(resData);
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setSalesLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    selectedDateRange,
    selectedCustomerType,
    search,
    tabChange,
    selectedCenter,
    currentPage,
  ]);

  const supplierLedgerColumns = [
    {
      title: "Date",
      render: (record) => record?.date,
    },
    {
      title: "Customer Type",
      dataIndex: "customer_type",
    },
    {
      title: "Customer",
      render: (record) =>
        record?.farmer ||
        record?.retailer ||
        record?.customer_name ||
        record?.corporate_customer_name,
    },
    {
      title: "Type",
      dataIndex: "voucher_type",
    },
    {
      title: "Particulars",
      dataIndex: "particulars",
    },

    {
      title: "Dr",
      render: (record) =>
        Number(record?.debit?.toFixed(2)).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
    },
    {
      title: "Cr",
      render: (record) =>
        Number(record?.credit?.toFixed(2)).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
    },
    {
      title: "Balance",
      render: (record) => {
        if (
          record?.calculated_balance === undefined ||
          record?.calculated_balance === null
        ) {
          return "0.00";
        }

        const balance = Number(record.calculated_balance.toFixed(2));
        const formatted = Math.abs(balance).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

        return balance >= 0 ? `${formatted} Dr` : `${formatted} Cr`;
      },
    },
  ];

  return (
    <>
      <div className="farmerDashboard">
        <div className="shadow-sm p-3">
          <div className="m-2 mt-4 d-flex justify-content-between align-items-center">
            <div>
              <Select
                className="me-3"
                placeholder="Select Center"
                style={{ width: "200px" }}
                onChange={(value) => setSelectedCenter(value)}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {centerList?.map((center) => (
                  <Option
                    value={center?.center?.employee_id}
                    key={center?.center?.employee_id}
                  >
                    {center?.center_name}
                  </Option>
                ))}
              </Select>
              <Select
                placeholder="Select Customer Type"
                className="me-3"
                onChange={(value) => setSelectedCustomerType(value)}
                style={{ width: "200px" }}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
                showSearch
                allowClear
              >
                {customerType?.map((type) => (
                  <Option value={type} key={type}>
                    {type}
                  </Option>
                ))}
              </Select>

              <RangePicker
                onChange={handleDateRange}
                value={
                  selectedDateRange
                    ? selectedDateRange.map((date) => dayjs(date))
                    : null
                }
                style={{ width: "320px" }}
              />
            </div>
          </div>
          <div className="m-2 mt-4 d-flex justify-content-start align-items-center">
            <Search
              className="w-25"
              placeholder="search customer / particulars here..."
              allowClear
              onSearch={(value) => setSearch(value)}
              enterButton={
                <Button
                  style={{
                    backgroundColor: "#46604A",
                    borderColor: "#46604A",
                    color: "white",
                  }}
                >
                  Search
                </Button>
              }
            />
          </div>
          <Table
            columns={supplierLedgerColumns}
            dataSource={supplierLedgers}
            loading={salesLoading}
            rowKey={(record) => record?.pl_id}
            size="small"
            pagination={{
              current: currentPage,
              pageSize: 10,
              total: totalItems,
              onChange: handlePageChange,
            }}
          />
        </div>
      </div>
    </>
  );
}
